import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DramaCard from '@/components/feature/DramaCard';
import { categories, dramas } from '@/mocks/dramas';

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [input, setInput] = useState(q);

  const keyword = q.trim().toLowerCase();

  const results = useMemo(() => {
    if (!keyword) return [];
    return dramas.filter((d) => {
      const haystack = [d.title, d.synopsis, d.starring.join(' '), d.genres.join(' '), d.categoryName, d.region, d.director]
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [keyword]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = input.trim();
    setParams(next ? { q: next } : {});
  };

  const reset = () => {
    setInput('');
    setParams({});
  };

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">搜索剧集</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">搜索电视剧</h1>
          <p className="mt-2 text-sm text-foreground-500">支持按剧名、主演、剧情关键词进行模糊匹配</p>

          <form onSubmit={submit} className="mt-5 flex max-w-2xl flex-col gap-3 sm:flex-row">
            <div className="flex h-12 flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-50 pl-4 pr-2">
              <i className="ri-search-line text-lg text-foreground-500" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入剧名或剧情关键词，如：权谋、悬疑、爱情"
                className="h-full flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="h-12 whitespace-nowrap rounded-full bg-primary-500 px-7 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
        {keyword ? (
          <>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                关键词「<strong className="text-primary-600">{q}</strong>」共找到{' '}
                <strong className="font-heading text-primary-600">{results.length}</strong> 部相关剧集
              </p>
              <button
                type="button"
                onClick={reset}
                className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-accent-600"
              >
                <i className="ri-refresh-line" />
                重置搜索
              </button>
            </div>

            {results.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {results.map((d) => (
                  <DramaCard key={d.id} drama={d} variant="list" />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <p className="mt-4 font-heading text-base font-semibold text-foreground-800">
                  没有找到与「{q}」相关的剧集
                </p>
                <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或从下方分类中浏览</p>
                <div className="mx-auto mt-6 flex max-w-xl flex-wrap items-center justify-center gap-2">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="rounded-full border border-background-200 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={reset}
                    className="h-10 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    重置搜索
                  </button>
                  <Link
                    to="/"
                    className="flex h-10 items-center whitespace-nowrap rounded-md border border-background-300 px-5 text-sm text-foreground-700"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-search-2-line text-2xl" />
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-800">输入关键词开始搜索</p>
            <p className="mt-1 text-sm text-foreground-500">你可以搜索剧名、主演，或输入剧情关键词</p>
            <div className="mx-auto mt-6 flex max-w-2xl flex-wrap items-center justify-center gap-2">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="rounded-full bg-secondary-100 px-4 py-2 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  浏览{c.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}